/**
 * @fileoverview The tray's move and removal handlers, moved word for word out
 * of PhotoTray (audit batch 18, A103/A104).
 *
 * A move is kept on the server (POST /tray/move) with an Undo in the toast
 * that puts each photo back; a removal hides the photo at once in what is drawn
 * while the server is asked (POST /tray/remove) and says "1 photo removed" in
 * a short toast with no Undo and no lasting bar: once removed, it is removed
 * (owner, 2026-10-21, B45, as B26 for plants). A step the server did not take is reported when it
 * failed on our side (reportServerFailure), said as the standard sentence or
 * the refusal's own words, and the tray is read again.
 *
 * Where: PhotoTray (the route /plants/photos).
 * API: POST /tray/move (moveInTray), /tray/remove (removeFromTray).
 * Settings: TRAY_MOVE_UNDO_TOAST_MS (config/tunables/garden).
 * Words (en/es): tray.pile.undo, tray.pile.moved, tray.pile.changeFailed, tray.pile.removedGroupOne, tray.pile.removedGroup.
 *
 * @module useTrayEdits
 */

import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/shared/components/ui/use-toast';
import { ToastAction } from '@/shared/components/ui/toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { moveInTray, removeFromTray, type Tray, type TrayMoveTarget, type TrayPhoto } from '@/shared/api/tray';
import { debugLog } from '@/config/config';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure, reportServerFailure } from '@/shared/lib/serverFailure';
import { TRAY_QUERY_KEY, putTray } from './useTray';
import { TRAY_MOVE_UNDO_TOAST_MS } from '@/config/tunables/garden';

/** What useTrayEdits is given. */
interface UseTrayEditsArgs {
  /** Every photo on the tray, removed ones included. */
  photos: TrayPhoto[];
  /** Closes the move sheet (called when a move starts). */
  closeMoving: () => void;
}

/** What useTrayEdits gives back. */
interface UseTrayEditsResult {
  /** Moves photos to another pile or one of their own, with an Undo in the toast. */
  movePhotos: (trayIds: number[], target: TrayMoveTarget, title: string) => void;
  /** Moves one photo to another pile or one of its own, with an Undo in the toast. */
  movePhoto: (photo: TrayPhoto, target: TrayMoveTarget) => void;
  /** Removes photos: they vanish at once, with a short toast and no Undo. */
  drop: (trayIds: number[]) => void;
}

/**
 * The move and removal handlers (and the move's Undo) for the tray.
 *
 * @param args - UseTrayEditsArgs.
 * @returns UseTrayEditsResult.
 */
export function useTrayEdits({ photos, closeMoving }: UseTrayEditsArgs): UseTrayEditsResult {
  const { reactTranslate: t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  /**
   * A move, removal or move Undo the server did not take: reported when it failed
   * on our side, and said (the standard sentence, or the refusal's own words).
   * The tray is read again, so what is drawn is what the server has.
   *
   * @param error - Whatever was thrown.
   * @param where - The step, for the report.
   */
  const changeFailed = (error: unknown, where: string): void => {
    debugLog('PhotoTray', `${where} failed`, error);
    if (isServerFailure(error)) reportServerFailure(error, where);
    toast({ title: errorMessage(error, t('tray.pile.changeFailed')), variant: 'destructive' });
    void queryClient.invalidateQueries({ queryKey: TRAY_QUERY_KEY });
  };

  /**
   * Moves photos to another pile or one of their own, kept on the server, with
   * an Undo in the toast that puts each back where it was.
   *
   * @param trayIds - The photos.
   * @param target - Where to: a pile's key, or 'own'.
   * @param title - What the toast says happened.
   */
  const movePhotos = (trayIds: number[], target: TrayMoveTarget, title: string): void => {
    const byId = new Map(photos.map((photo) => [photo.tray_id, photo]));
    const before = trayIds.map((trayId) => ({ trayId, to: byId.get(trayId)?.moved_to ?? null }));
    closeMoving();
    moveInTray(trayIds.map((trayId) => ({ trayId, to: target })))
      .then((tray) => {
        putTray(queryClient, tray);
        toast({
          title,
          duration: TRAY_MOVE_UNDO_TOAST_MS,
          action: (
            <ToastAction
              altText={t('tray.pile.undo')}
              onClick={() => { moveInTray(before).then((was) => putTray(queryClient, was)).catch((error) => changeFailed(error, 'Tray move undo')); }}
            >
              {t('tray.pile.undo')}
            </ToastAction>
          ),
        });
      })
      .catch((error) => changeFailed(error, 'Tray move'));
  };
  /**
   * Moves one photo to another pile or one of its own, with an Undo in the toast.
   *
   * @param photo - The photo.
   * @param target - A pile's key, or 'own'.
   */
  const movePhoto = (photo: TrayPhoto, target: TrayMoveTarget): void => movePhotos([photo.tray_id], target, t('tray.pile.moved'));

  /**
   * Marks photos removed in what is drawn while the server is asked, so they
   * vanish at once (drawPiles leaves a removed photo out).
   *
   * @param trayIds - The photos.
   */
  const markRemoved = (trayIds: number[]): void => {
    const ids = new Set(trayIds);
    queryClient.setQueriesData<Tray>({ queryKey: TRAY_QUERY_KEY }, (current) => current && {
      ...current,
      photos: current.photos.map((photo) => (ids.has(photo.tray_id) ? { ...photo, removed: true } : photo)),
    });
  };
  /**
   * Removes photos: they vanish at once and a short toast says so, with no
   * Undo and no lasting bar (POST /tray/remove; the server's own 24-hour undo
   * is left alone and unused by the app). Once removed, it is removed.
   *
   * @param trayIds - The photos.
   */
  const drop = (trayIds: number[]): void => {
    markRemoved(trayIds);
    toast({ title: trayIds.length === 1 ? t('tray.pile.removedGroupOne') : t('tray.pile.removedGroup', { count: trayIds.length }) });
    removeFromTray(trayIds).then((tray) => putTray(queryClient, tray)).catch((error) => changeFailed(error, 'Tray remove'));
  };
  return { movePhotos, movePhoto, drop };
}
